import {
  ApplicationConfig,
  EnvironmentProviders,
  ErrorHandler,
  importProvidersFrom,
  LOCALE_ID,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';

import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { APP_BASE_HREF, DATE_PIPE_DEFAULT_OPTIONS, DatePipeConfig } from '@angular/common';
import { MAT_BOTTOM_SHEET_DEFAULT_OPTIONS } from '@angular/material/bottom-sheet';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_PAGINATOR_DEFAULT_OPTIONS } from '@angular/material/paginator';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsModule } from '@ngxs/store';
import { GROWTH_BOOK_CONFIG } from '@x/common/ab-test/ab-test.config';
import { AbTestModule } from '@x/common/ab-test/ab-test.module';
import { BarcodeModule } from '@x/common/barcode';
import { BrowserModule } from '@x/common/browser';
import { CloudinaryModule } from '@x/common/cloudinary';
import { DateTimeModule, Timezone } from '@x/common/datetime';
import { ErrorModule, ErrorResponseService } from '@x/common/error';
import { FormCommonModule } from '@x/common/form';
import { GraphModule } from '@x/common/graph';
import { IconModule } from '@x/common/icon';
import { LoaderModule } from '@x/common/loader';
import { LocaleModule } from '@x/common/locale';
import { MAP_CONFIG } from '@x/common/map/map';
import { MetaModule } from '@x/common/meta';
import { MoneyModule } from '@x/common/money';
import { OperationModule } from '@x/common/operation';
import { ReferralModule } from '@x/common/referral/referral.module';
import { ResponsiveModule } from '@x/common/responsive';
import { ScrollModule } from '@x/common/scroll/scroll.module';
import { StorageModule } from '@x/common/storage';
import { TRACKING_SHOP_CONTEXT, TrackingModule } from '@x/common/tracking';
import { ContentClientModule } from '@x/content/client';
import { ContentRenderModule } from '@x/content/render';
import { routes } from '@x/ecommerce-shop/app/app.routes';
import { AddressModule } from '@x/ecommerce-shop/app/core/address/address.module';
import { AuthModule } from '@x/ecommerce-shop/app/core/auth/auth.module';
import { AuthService } from '@x/ecommerce-shop/app/core/auth/services/auth.service';
import { SHOP_CONTENT_DYNAMIC_COMPONENTS } from '@x/ecommerce-shop/app/core/content/shop-content-dynamic-components';
import { GrowthBookContextService } from '@x/ecommerce-shop/app/core/growth-book/growth-book-context.service';
import { MenuModule } from '@x/ecommerce-shop/app/core/menu/menu.module';
import { OrderModule } from '@x/ecommerce-shop/app/core/order/order.module';
import { ReferrerModule } from '@x/ecommerce-shop/app/core/referrer/referrer.module';
import { RegionModule } from '@x/ecommerce-shop/app/core/region/region.module';
import { SearchModule } from '@x/ecommerce-shop/app/core/search';
import { ShopErrorHandlerService } from '@x/ecommerce-shop/app/core/shop-error/shop-error-handler/shop-error-handler.service';
import { ShopErrorModule } from '@x/ecommerce-shop/app/core/shop-error/shop-error.module';
import { ShopMetaModule } from '@x/ecommerce-shop/app/core/shop-meta/shop-meta.module';
import { ShopOverlayModule } from '@x/ecommerce-shop/app/core/shop-overlay/shop-overlay.module';
import { ShopScrollModule } from '@x/ecommerce-shop/app/core/shop-scroll/shop-scroll.module';
import { ShopSnackbarModule } from '@x/ecommerce-shop/app/core/shop-snackbar/shop-snackbar.module';
import { TrackingShopContextService } from '@x/ecommerce-shop/app/core/tracking/tracking-shop-context.service';
import { WishlistModule } from '@x/ecommerce-shop/app/core/whishlist/wishlist.module';
import { SubscriberModule } from '@x/ecommerce-shop/app/subscriber-checkout/subscriber.module';
import { IShopConfig, SHOP_CONFIG } from '@x/ecommerce-shop/config/shop.config';
import { EcommerceBackendUrlModule } from '@x/ecommerce/backend-url';
import { AUTHORIZATION_HEADER_PROVIDER, EcommerceShopClientModule } from '@x/ecommerce/shop-client';
import { GeocodeClientModule } from '@x/geocode/client';
import { SHOP_LANGUAGE } from './core/language/shop-language';

const environmentProviders = (shopConfig: IShopConfig): EnvironmentProviders[] => [
  importProvidersFrom(
    ErrorModule.forRoot(),
    EcommerceShopClientModule.forRoot(shopConfig.ecommerceShopClient),
    GeocodeClientModule.forRoot(shopConfig.geocodeClient),
    EcommerceBackendUrlModule.forRoot(shopConfig.ecommerceBackend),
    GraphModule.forRoot(),
    NgxsModule.forRoot([], { developmentMode: !shopConfig.production }),
    NgxsRouterPluginModule.forRoot(),
    NgxsReduxDevtoolsPluginModule.forRoot(),
    BrowserAnimationsModule,
    ResponsiveModule,
    FormCommonModule,
    OperationModule.forRoot(),
    StorageModule.forRoot(shopConfig.storageConfig),
    IconModule.forRoot(shopConfig.iconConfig),
    MoneyModule.forRoot(shopConfig.money),
    CloudinaryModule.forRoot(shopConfig.cloudinary),
    ReferralModule.forRoot(shopConfig.referral),
    TrackingModule.forRoot(shopConfig.tracking),
    //core
    AuthModule.forRoot(),
    OrderModule,
    RegionModule,
    ShopErrorModule.forRoot(),
    ContentClientModule.forRoot(shopConfig.contentClient),
    ContentRenderModule.forRoot({
      brand: shopConfig.brand,
      components: SHOP_CONTENT_DYNAMIC_COMPONENTS,
    }),
    MetaModule.forRoot(shopConfig.meta),
    ShopSnackbarModule,
    MenuModule,
    LoaderModule,
    BarcodeModule.forRoot(shopConfig.barcode),
    SubscriberModule,
    ShopScrollModule,
    FontAwesomeModule,
    AddressModule,
    ReferrerModule.forRoot({
      queryParameters: ['referrer', 'utm_referrer'],
      storageKey: 'referrer',
    }),
    ShopMetaModule,
    ShopOverlayModule,
    WishlistModule,
    ScrollModule,
    AbTestModule,
    SearchModule,
    LocaleModule.forRoot([SHOP_LANGUAGE]),
    DateTimeModule.forRoot({
      timezone: Timezone.localZone(),
      format: shopConfig.date.format ?? 'medium',
    }),
    BrowserModule.forRoot(shopConfig.browser),
  ),
];

export const appConfig = (shopConfig: IShopConfig): ApplicationConfig => ({
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'fill',
        subscriptSizing: 'dynamic',
      },
    },
    {
      provide: MAT_BOTTOM_SHEET_DEFAULT_OPTIONS,
      useValue: { scrollStrategy: new NoopScrollStrategy() },
    },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { scrollStrategy: new NoopScrollStrategy() } },
    { provide: MAT_SELECT_CONFIG, useValue: { hideSingleSelectionIndicator: true } },
    { provide: MAT_PAGINATOR_DEFAULT_OPTIONS, useValue: { formFieldAppearance: 'fill' } },
    { provide: AUTHORIZATION_HEADER_PROVIDER, useClass: AuthService },
    { provide: MAP_CONFIG, useValue: shopConfig.googleMaps },
    { provide: LOCALE_ID, useValue: shopConfig.defaultLocale },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: {
        dateFormat: shopConfig.date.format,
      } satisfies DatePipeConfig,
    },
    { provide: ErrorHandler, useClass: ShopErrorHandlerService },
    { provide: GROWTH_BOOK_CONFIG, useClass: GrowthBookContextService },
    { provide: TRACKING_SHOP_CONTEXT, useClass: TrackingShopContextService },
    { provide: APP_BASE_HREF, useValue: '/' },
    {
      provide: ErrorResponseService,
      useValue: new ErrorResponseService(),
    },
    {
      provide: SHOP_CONFIG,
      useValue: shopConfig,
    },
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
        onSameUrlNavigation: 'reload',
      }),
    ),
    // provideClientHydration(
    //   withHttpTransferCacheOptions({
    //     includePostRequests: true,
    //     filter: (req) => {
    //       return !req.headers.has('Authorization');
    //     },
    //   }),
    // ),
    ...environmentProviders(shopConfig),
  ],
});
